import type { TransformFamily, FormFieldDef } from '@capture/shared';
import type { TransformPluginRow } from '@/features/admin/transform-plugins/api/transform-plugins-client';

/**
 * Display shape for a published tenant transform plugin inside the
 * transform-chain step picker — mirrors the subset of TransformOpDef the
 * picker/step-config UI actually reads. Deliberately does NOT carry an
 * `evaluate`/`validate` function: plugin evaluation only runs server-side
 * (DSL expression + host capabilities), so preview/validation for plugin
 * steps is handled separately (knownExternalOpIds passthrough, no local
 * evaluate). See F-043.
 */
export interface PluginPickerOp {
  id: string;
  family: TransformFamily;
  label: string;
  description: string;
  defaultConfig: Record<string, unknown>;
  formSchema: FormFieldDef[];
  isPlugin: true;
}

export function pluginRowToPickerOp(row: TransformPluginRow): PluginPickerOp {
  return {
    id: row.pluginId,
    family: row.family as TransformFamily,
    label: row.label,
    description: row.description,
    defaultConfig: (row.defaultConfig as Record<string, unknown>) ?? {},
    formSchema: Array.isArray(row.formSchema) ? (row.formSchema as FormFieldDef[]) : [],
    isPlugin: true,
  };
}

const EMPTY_FAMILY_GROUPS: Record<TransformFamily, PluginPickerOp[]> = {
  string: [], literal: [], 'cross-field': [], logic: [], lookup: [], number: [], date: [],
};

/**
 * Groups published plugins into their declared family, mapped to the picker
 * display shape — shared by both step pickers (TransformChainDrawer's inline
 * OpPicker and StepsEditor's named-chain OpPicker) so the two don't carry
 * independent copies of the same grouping loop.
 */
export function groupPluginOpsByFamily(rows: TransformPluginRow[]): Record<TransformFamily, PluginPickerOp[]> {
  const groups: Record<TransformFamily, PluginPickerOp[]> = { ...EMPTY_FAMILY_GROUPS };
  for (const key of Object.keys(groups) as TransformFamily[]) groups[key] = [];
  for (const row of rows) {
    const op = pluginRowToPickerOp(row);
    (groups[op.family] ??= []).push(op);
  }
  return groups;
}

/** Case-insensitive match on id or label — the same predicate both pickers'
 *  search boxes apply to plugin ops. */
export function filterPluginOps(ops: PluginPickerOp[], query: string): PluginPickerOp[] {
  const q = query.toLowerCase();
  if (!q) return ops;
  return ops.filter((op) => op.id.toLowerCase().includes(q) || op.label.toLowerCase().includes(q));
}
